export class NamedEntity
{
    DisplayName: string; // Name to display in UI

    constructor(displayName: string)
    {
        this.DisplayName = displayName;
    }

    static IsEqual(entity1: NamedEntity, entity2: NamedEntity): boolean { return entity1.DisplayName === entity2.DisplayName; }
    static TransformFunc(entity: NamedEntity): string { return entity.DisplayName; }
};

export class Suit extends NamedEntity
{
    Color: string; // CSS color for the suit
    TrickCost: number; // Cost of a trick in this suit
    FirstTrickBonus: number; // Bonus for winning the first trick in this suit

    constructor(displayName: string, color: string, trickCost: number, firstTrickBonus: number = 0)
    {
        super(displayName);

        this.Color = color;
        this.TrickCost = trickCost;
        this.FirstTrickBonus = firstTrickBonus;
    }

    static readonly Clubs = new Suit('&clubs;', 'black', 20);
    static readonly Diamonds = new Suit('&diams;', 'red', 20);
    static readonly Hearts = new Suit('&hearts;', 'red', 30);
    static readonly Spades = new Suit('&spades;', 'black', 30);
    static readonly NoTrump = new Suit('NT', 'gray', 30, 10);
};

export const Suits: Suit[] = [Suit.Clubs, Suit.Diamonds, Suit.Hearts, Suit.Spades, Suit.NoTrump];

export class ContractDealer extends NamedEntity
{
    constructor(displayName: string)
    {
        super(displayName);
    }

    static readonly North = new ContractDealer('N');
    static readonly East = new ContractDealer('E');
    static readonly South = new ContractDealer('S');
    static readonly West = new ContractDealer('W');
}

export const ContractDealers: ContractDealer[] = [ContractDealer.North, ContractDealer.East, ContractDealer.South, ContractDealer.West];

export class ContractType extends NamedEntity
{
    constructor(displayName: string)
    {
        super(displayName);
    }

    static readonly Pass = new ContractType('');
    static readonly Double = new ContractType('X');
    static readonly Redouble = new ContractType('XX');
 }

export const ContractTypes: ContractType[] = [ContractType.Pass, ContractType.Double, ContractType.Redouble];

export class Vulnerability extends NamedEntity
{
    constructor(displayName: string)
    {
        super(displayName);
    }

    static readonly None = new Vulnerability('&mdash;');
    static readonly NorthSouth = new Vulnerability('NS');
    static readonly WestEast = new Vulnerability('WE');
    static readonly All = new Vulnerability('ALL');

    static FromDealNumber(dealNumber: number): Vulnerability
    {
        const pos = Math.floor((dealNumber - 1) / Vulnerabilities.length + dealNumber - 1) % Vulnerabilities.length;
        return Vulnerabilities[pos]!;
    }

    IsVulnerable(dealer: ContractDealer)
    {
        if (NamedEntity.IsEqual(this, Vulnerability.All))
            return true;
        if (NamedEntity.IsEqual(this, Vulnerability.NorthSouth))
            return NamedEntity.IsEqual(ContractDealer.North, dealer) || NamedEntity.IsEqual(ContractDealer.South, dealer);
        if (NamedEntity.IsEqual(this, Vulnerability.WestEast))
            return NamedEntity.IsEqual(ContractDealer.East, dealer) || NamedEntity.IsEqual(ContractDealer.West, dealer);
        return false;
    }

    IsOpponentsVulnerable(dealer: ContractDealer)
    {
        if (NamedEntity.IsEqual(this, Vulnerability.All))
            return true;
        if (NamedEntity.IsEqual(this, Vulnerability.NorthSouth))
            return NamedEntity.IsEqual(ContractDealer.East, dealer) || NamedEntity.IsEqual(ContractDealer.West, dealer);
        if (NamedEntity.IsEqual(this, Vulnerability.WestEast))
            return NamedEntity.IsEqual(ContractDealer.North, dealer) || NamedEntity.IsEqual(ContractDealer.South, dealer);
        return false;
    }

}

export const Vulnerabilities: Vulnerability[] = [Vulnerability.None, Vulnerability.NorthSouth, Vulnerability.WestEast, Vulnerability.All];

//---------------------------------------------------------------------------------------------------------------------
export class Contract
{
    Level: number; // 1..7
    Suit: Suit;
    Type: ContractType;
    Dealer: ContractDealer;

    constructor(level: number, suit: Suit, type: ContractType, dealer: ContractType)
    {
        this.Level = level;
        this.Suit = suit;
        this.Type = type;
        this.Dealer = dealer;
    }

    #baseScore()
    {
        let score = this.Level * this.Suit.TrickCost + this.Suit.FirstTrickBonus;
        if (NamedEntity.IsEqual(ContractType.Double, this.Type))
            score *= 2;
        if (NamedEntity.IsEqual(ContractType.Redouble, this.Type))
            score *= 4;
        return score;
    }

    // relativeTricks: -7..+6 (relative tricks: 0 = just made, negative = down, positive = overtricks)
    Score(vulnerability: Vulnerability, relativeTricks: number): number
    {
        let vuln = vulnerability.IsVulnerable(this.Dealer);
        return relativeTricks >= 0 ?
            this.#madeContractScore(vuln, relativeTricks) :
            this.#downContractScore(vuln, -relativeTricks);
    }

    #overtrickCost(vuln: boolean): number
    {
        if (NamedEntity.IsEqual(ContractType.Double, this.Type))
            return vuln ? 200 : 100;
        else if (NamedEntity.IsEqual(ContractType.Redouble, this.Type))
            return vuln ? 400 : 200;
        else
            return this.Suit.TrickCost;
    }

    #bonusScore(isGame: boolean, vuln: boolean)
    {
        let bonus = 0;
        // Partscore/game
        if (isGame)
            bonus += vuln ? 500 : 300; // Game bonus
        else
            bonus += 50; // Partscore bonus

        // Slam bonuses
        if (this.Level == 6)
            bonus += vuln ? 750 : 500; // Small slam
        if (this.Level == 7)
            bonus += vuln ? 1500 : 1000; // Grand slam

        // Doubled/redoubled contract bonus
        if (NamedEntity.IsEqual(ContractType.Double, this.Type))
            bonus += 50;
        if (NamedEntity.IsEqual(ContractType.Redouble, this.Type))
            bonus += 100;
        return bonus;
    }

    #madeContractScore(vuln: boolean, overtricks: number)
    {
        let contractPoints = this.#baseScore();
        return contractPoints + overtricks * this.#overtrickCost(vuln) + this.#bonusScore(contractPoints >= 100, vuln);
    }

    #downContractScore(vuln: boolean, undertricks: number)
    {
        let score = 0;
        // Undertricks
        if (NamedEntity.IsEqual(ContractType.Pass, this.Type))
        {
            // Not doubled
            score -= vuln ? 100 * undertricks : 50 * undertricks;
        }
        else
        {
            // Doubled or redoubled
            let first = vuln ? 200 : 100;
            let second_third = vuln ? 300 : 200;
            let rest = 300;
            let penalty = 0;
            let mult = NamedEntity.IsEqual(ContractType.Double, this.Type) ? 1 : 2;
            if (undertricks >= 1)
                penalty += first;
            if (undertricks >= 2)
                penalty += second_third;
            if (undertricks >= 3)
                penalty += second_third;
            if (undertricks > 3)
                penalty += (undertricks - 3) * rest;
            score -= penalty * mult;
        }
        return score;
    }
};

//----------------------------------------------------------------------------------------
export class DealResults
{
    Vulnerability: Vulnerability; // Vulnerability of the deal
    Dealer: ContractDealer; // Dealer of the deal   
    Score: number; // Score of the deal
    PC: number; // Playing Cards (PC) value of the deal
    Compensation: number; // Compensation for the deal
    Delta: number; // Delta = Score - Compensation
    IMPs: number; // IMPs for the deal

    constructor(vulnerability: Vulnerability, dealer: ContractDealer, score: number, pc: number)
    {
        this.Vulnerability = vulnerability;
        this.Dealer = dealer;
        this.Score = score;
        this.PC = pc;
        this.Compensation = this.#calcCompensation();
        this.Delta = this.Score - this.Compensation;
        this.IMPs = this.#calcIMPs();
    }

    static #calcBaseCompensation(isVuln: boolean, pc: number): number
    {
        if (pc < 20 || pc > 40)
            throw new Error("Invalid PC value: " + pc);

        const CompensationTable = [
            [36, 1800, 1200],
            [35, 1650, 1100],
            [34, 1500, 1000],
            [33, 1350, 900],
            [32, 1150, 750],
            [31, 900, 600],
            [30, 690, 490],
            [29, 660, 460],
            [28, 630, 430],
            [27, 600, 400],
            [26, 520, 350],
            [25, 440, 300],
            [24, 290, 200],
            [23, 110, 110],
            [22, 70, 70],
            [21, 50, 50],
            [20, 0, 0]
        ];
        let compPc: number, compVuln: number, compNotVuln: number;
        for (let value of CompensationTable)
            if (pc >= value[0]!)
                return isVuln ? value[1]! : value[2]!;

        throw new Error("Internal Error");
    }

    #calcCompensation(): number
    {
        if (this.PC >= 20)
        {
            let isVuln = this.Vulnerability.IsVulnerable(this.Dealer);
            return DealResults.#calcBaseCompensation(isVuln, this.PC);
        }
        else
        {
            let isVuln = this.Vulnerability.IsOpponentsVulnerable(this.Dealer);
            return -DealResults.#calcBaseCompensation(isVuln, 40 - this.PC);
        }
    }

    #calcIMPs(): number
    {
        const IMPs = [20, 50, 90, 130, 170, 220, 270, 320, 370, 430, 500, 600, 750, 900, 1100, 1300, 1500, 1750, 2000, 2250, 2500, 3000, 3500, 99999];
        let absDelta = Math.abs(this.Delta);
        let imp = 0;
        for (let i = 0; i < IMPs.length; i++)
        {
            if (absDelta < IMPs[i]!)
            {
                imp = i;
                break;
            }
        }
        return this.Delta > 0 ? imp : -imp;
    }
};
